import { useMemo } from 'react';

import type {
  ComponentPageData,
  CourseQuestionDefaults,
  RawComponent,
  RawQuestionSettings,
} from '@/features/content/useComponent';
import { resolveSolutionsSetting } from '@/features/questions/questionTimingSettings';

export interface ComponentTimeline {
  isContentVisible: boolean;
  contentVisibleDate: Date | null;

  isHomeworkVisible: boolean;
  isHomeworkOpen: boolean;
  isHomeworkClosed: boolean;
  homeworkStartDate: Date | null;
  homeworkEndDate: Date | null;

  isPreLectureVisible: boolean;
  isPreLectureOpen: boolean;
  isPreLectureClosed: boolean;
  preLectureStartDate: Date | null;
  preLectureEndDate: Date | null;

  areHomeworkSolutionsVisible: boolean;
  arePreLectureSolutionsVisible: boolean;
}

export type ComponentTimelineSource =
  | Pick<ComponentPageData, 'courseDefaults' | 'rawComponent'>
  | RawComponent
  | null
  | undefined;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function normalizeString(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }

  const normalized = value.trim();
  return normalized.length > 0 ? normalized : null;
}

function normalizeNumber(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  const normalized = normalizeString(value);

  if (!normalized) {
    return undefined;
  }

  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function normalizeBoolean(value: unknown): boolean | undefined {
  return typeof value === 'boolean' ? value : undefined;
}

function resolveRawComponent(source: ComponentTimelineSource) {
  if (!source) {
    return null;
  }

  if (isRecord(source) && 'rawComponent' in source && isRecord(source.rawComponent)) {
    return source.rawComponent as RawComponent;
  }

  return source as RawComponent;
}

function resolveCourseDefaults(
  source: ComponentTimelineSource,
): CourseQuestionDefaults | undefined {
  return isRecord(source) && 'rawComponent' in source && isRecord(source.rawComponent)
    ? (source.courseDefaults as CourseQuestionDefaults | undefined)
    : undefined;
}

function getTimestampDate(value: unknown) {
  const timestamp = parseTimestamp(value);
  return timestamp !== null ? new Date(timestamp) : null;
}

export function parseTimestamp(value: unknown): number | null {
  const parsed = normalizeNumber(value);
  return parsed !== undefined && parsed > 0 ? parsed : null;
}

function asSettings(value: unknown): RawQuestionSettings | null {
  return isRecord(value) ? (value as RawQuestionSettings) : null;
}

// When a section's solutions show: the chapter's own value if it has one, the
// course's otherwise, one setting at a time. A chapter's settings used to hide
// the course's whole, and the course's never arrived here (issue #144).
export function getSolutionsInterval(
  component: RawComponent,
  courseDefaults: CourseQuestionDefaults | undefined,
  kind: 'homework' | 'preLecture',
): number {
  if (kind === 'preLecture') {
    return resolveSolutionsSetting(
      asSettings(
        component.pre_lecture_question_settings ??
          component.preLectureQuestionSettings,
      ),
      asSettings(
        courseDefaults?.coursePrelectureQuestionSettings ??
          component.course_prelecture_question_settings ??
          component.coursePrelectureQuestionSettings,
      ),
    ).value;
  }

  return resolveSolutionsSetting(
    asSettings(component.question_settings ?? component.questionSettings),
    asSettings(
      courseDefaults?.courseQuestionSettings ??
        component.course_question_settings ??
        component.courseQuestionSettings,
    ),
  ).value;
}

function getVisibilityFromTimestampOrFlag(
  now: number,
  timestamp: number | null,
  fallbackFlag: boolean,
) {
  return timestamp !== null ? now >= timestamp : fallbackFlag;
}

function getClosedFromTimestampOrFlag(
  now: number,
  timestamp: number | null,
  fallbackFlag: boolean,
) {
  return timestamp !== null ? now >= timestamp : fallbackFlag;
}

export function getSolutionVisibilityTimestamp(
  endTimeUtcMilli: number | null,
  interval: number | undefined,
): number | undefined {
  if (endTimeUtcMilli === null || interval === 4) {
    return undefined;
  }

  if (interval === 1) {
    return endTimeUtcMilli + 60 * 60 * 1000;
  }

  if (interval === 2) {
    return endTimeUtcMilli + 24 * 60 * 60 * 1000;
  }

  if (interval === 3) {
    return endTimeUtcMilli + 7 * 24 * 60 * 60 * 1000;
  }

  return endTimeUtcMilli;
}

export function areSolutionsVisible(
  now: number,
  endTimeUtcMilli: number | null,
  isClosedByFlagWithoutTimestamp: boolean,
  interval: number | undefined,
) {
  if (interval === 4) {
    return false;
  }

  if (endTimeUtcMilli !== null) {
    const visibleAt = getSolutionVisibilityTimestamp(endTimeUtcMilli, interval);
    return visibleAt !== undefined && now >= visibleAt;
  }

  return isClosedByFlagWithoutTimestamp;
}

export function getComponentTimeline(
  source: ComponentTimelineSource,
  now = Date.now(),
): ComponentTimeline {
  const component = resolveRawComponent(source);
  const courseDefaults = resolveCourseDefaults(source);

  if (!component) {
    return {
      areHomeworkSolutionsVisible: false,
      arePreLectureSolutionsVisible: false,
      contentVisibleDate: null,
      homeworkEndDate: null,
      homeworkStartDate: null,
      isContentVisible: false,
      isHomeworkClosed: false,
      isHomeworkOpen: false,
      isHomeworkVisible: false,
      isPreLectureClosed: false,
      isPreLectureOpen: false,
      isPreLectureVisible: false,
      preLectureEndDate: null,
      preLectureStartDate: null,
    };
  }

  const homeworkStartTimeUtcMilli = parseTimestamp(
    component.start_time_utc_milli ?? component.startTimeUtcMilli,
  );
  const homeworkEndTimeUtcMilli = parseTimestamp(
    component.end_time_utc_milli ?? component.endTimeUtcMilli,
  );
  const isHomeworkVisibleByFlag =
    normalizeBoolean(component.is_live ?? component.isLive) === true;
  const isHomeworkClosedByFlag =
    normalizeBoolean(component.is_complete ?? component.isComplete) === true;
  const isPreLectureVisibleByFlag =
    normalizeBoolean(
      component.is_pre_lecture_live ?? component.isPreLectureLive,
    ) === true;
  const isPreLectureClosedByFlag =
    normalizeBoolean(
      component.is_pre_lecture_complete ?? component.isPreLectureComplete,
    ) === true;
  const effectiveContentStartTimeUtcMilli =
    parseTimestamp(
      component.content_start_time_utc_milli ?? component.contentStartTimeUtcMilli,
    ) ?? homeworkStartTimeUtcMilli;
  const effectivePreLectureStartTimeUtcMilli =
    parseTimestamp(
      component.pre_lecture_start_time_utc_milli ??
        component.preLectureStartTimeUtcMilli,
    ) ?? homeworkStartTimeUtcMilli;
  const preLectureEndTimeUtcMilli = parseTimestamp(
    component.pre_lecture_end_time_utc_milli ??
      component.preLectureEndTimeUtcMilli,
  );

  const homeworkInterval = getSolutionsInterval(component, courseDefaults, 'homework');
  const preLectureInterval = getSolutionsInterval(component, courseDefaults, 'preLecture');
  // LEGACY SUPPORT: some components were closed with the old "close Now" action,
  // which set is_live=false + is_complete=true but never wrote any timestamps (no
  // content/start/end times). Such a component is a COMPLETED assignment that must
  // still be visible to students — read-only, not open to new submissions. The
  // current UI always writes timestamps when making a component visible or closing
  // it (content_start_time_utc_milli + is_live, or end_time_utc_milli), so this
  // only ever matches those legacy records and does not change current behavior.
  const isLegacyClosedVisible =
    !isHomeworkVisibleByFlag &&
    isHomeworkClosedByFlag &&
    homeworkEndTimeUtcMilli === null &&
    effectiveContentStartTimeUtcMilli === null;
  const isContentVisible = getVisibilityFromTimestampOrFlag(
    now,
    effectiveContentStartTimeUtcMilli,
    isHomeworkVisibleByFlag || isLegacyClosedVisible,
  );
  const isHomeworkVisible = getVisibilityFromTimestampOrFlag(
    now,
    homeworkStartTimeUtcMilli,
    isHomeworkVisibleByFlag,
  );
  const isHomeworkClosed = getClosedFromTimestampOrFlag(
    now,
    homeworkEndTimeUtcMilli,
    isHomeworkClosedByFlag,
  );
  const isPreLectureVisible = getVisibilityFromTimestampOrFlag(
    now,
    effectivePreLectureStartTimeUtcMilli,
    isPreLectureVisibleByFlag,
  );
  const isPreLectureClosed = getClosedFromTimestampOrFlag(
    now,
    preLectureEndTimeUtcMilli,
    isPreLectureClosedByFlag,
  );

  return {
    areHomeworkSolutionsVisible: areSolutionsVisible(
      now,
      homeworkEndTimeUtcMilli,
      homeworkEndTimeUtcMilli === null && isHomeworkClosedByFlag,
      homeworkInterval,
    ),
    arePreLectureSolutionsVisible: areSolutionsVisible(
      now,
      preLectureEndTimeUtcMilli,
      preLectureEndTimeUtcMilli === null && isPreLectureClosedByFlag,
      preLectureInterval,
    ),
    contentVisibleDate: getTimestampDate(effectiveContentStartTimeUtcMilli),
    homeworkEndDate: getTimestampDate(homeworkEndTimeUtcMilli),
    homeworkStartDate: getTimestampDate(homeworkStartTimeUtcMilli),
    isContentVisible,
    isHomeworkClosed,
    isHomeworkOpen: isHomeworkVisible && !isHomeworkClosed,
    isHomeworkVisible,
    isPreLectureClosed,
    isPreLectureOpen: isPreLectureVisible && !isPreLectureClosed,
    isPreLectureVisible,
    preLectureEndDate: getTimestampDate(preLectureEndTimeUtcMilli),
    preLectureStartDate: getTimestampDate(effectivePreLectureStartTimeUtcMilli),
  };
}

export function useComponentTimeline(
  source: ComponentTimelineSource,
  now = Date.now(),
) {
  return useMemo(() => getComponentTimeline(source, now), [source, now]);
}

/**
 * Student view "show hidden content": treat everything that visibility
 * settings hide from students as visible, WITHOUT touching any real state —
 * closed/due flags and solution visibility keep their true values (open
 * follows the same visible-and-not-closed derivation as the real timeline).
 * Purely a display transform; no setting is ever written.
 */
export function revealTimelineVisibility(
  timeline: ComponentTimeline,
): ComponentTimeline {
  return {
    ...timeline,
    isContentVisible: true,
    isHomeworkVisible: true,
    isHomeworkOpen: !timeline.isHomeworkClosed,
    isPreLectureVisible: true,
    isPreLectureOpen: !timeline.isPreLectureClosed,
  };
}

/**
 * Whether visibility settings currently hide any of this component's content
 * from students: the lesson content itself, or a question set that is neither
 * visible nor closed (closed sets already render read-only for students).
 */
export function timelineHasHiddenContent(
  timeline: ComponentTimeline,
  options: { hasHomework: boolean; hasPreLecture: boolean },
): boolean {
  return (
    !timeline.isContentVisible ||
    (options.hasHomework &&
      !timeline.isHomeworkVisible &&
      !timeline.isHomeworkClosed) ||
    (options.hasPreLecture &&
      !timeline.isPreLectureVisible &&
      !timeline.isPreLectureClosed)
  );
}
